import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { motion } from "motion/react";
import React from "react";

import { PositionWalkthrough } from "@/pages/AddPosition/PositionWalkthrough";

/**
 * YaaS Client Console — Positions page empty state.
 *
 * The first-run / zero-positions surface for the Positions page. Rather
 * than render a zeroed key-stats row + filter tabs over an empty table
 * (which reads as broken), the page hands this region over to a warm,
 * guiding onboarding screen modelled on best-in-class SaaS first-runs
 * (Stripe/Linear/Notion): explain what a position *is*, walk the user
 * through the add-position flow inline, and give one obvious primary
 * action that funnels into the real wizard.
 *
 * The five steps mirror `InlineAddPosition`'s flow so the inline preview
 * is an honest map of what the user is about to do, not decoration. The
 * region is a **2-panel progressive flow**: a left numbered step list
 * tracks progress (completed steps clickable to go back, current + future
 * steps locked), paired with a right panel that builds a **vertical stack**
 * of step sections — completed steps collapse to a compact summary row that
 * keeps the chosen value visible, the current step is expanded and
 * interactive, and clicking "Next" rolls the current step up into its
 * summary while the next step's content reveals below it.
 *
 * The first three steps are genuinely interactive (find the position, verify
 * ownership, set a rewards destination); the last step is a review receipt of
 * the discovered position plus the in-content "Submit Position" button.
 * "Next" is gated on the current step being satisfied; on the last step the
 * footer "Next" is disabled — submission happens via the in-content button,
 * which on success calls `onSubmitted()` to drop the empty-state override
 * and reveal the normal positions table.
 *
 * Surfaces stay flat/editorial per the yaas-styles skill — no raised
 * cards for the step list; the right panel is a single thin-bordered frame
 * on the ground surface. Ember is earned exactly once (the active step's
 * leading marker + dot) — "one surprising thing per screen."
 *
 * The orchestrator owns all wizard state + gating; the left accordion is
 * extracted to `StepAccordion` and the right stack + footer (and all the
 * per-step content/collapse machinery) to `StepFlowPanel`.
 */

interface PositionsEmptyStateProps {
    /** Hide the first-wallet introduction once the saved position is visible. */
    showIntro?: boolean;
    /** The review receipt is showing. It draws its own full-bleed top rule,
     *  so this section's rule and padding step aside rather than doubling
     *  the line and pushing it below the header's edge. */
    showReviewReceipt?: boolean;
    /** When true, the primary CTA is disabled and a desktop hint is shown. */
    viewOnly: boolean;
    /** Called once the in-content "Submit Position" flow completes — clears the
     *  empty-state override so the normal positions table renders. */
    onSubmitted: () => void;
    onPendingReviewChange?: (pending: boolean) => void;
}

export function PositionsEmptyState({
    showIntro = true,
    showReviewReceipt = false,
    viewOnly,
    onSubmitted,
    onPendingReviewChange
}: PositionsEmptyStateProps): React.ReactElement {
    return (
        <motion.section
            aria-label={showIntro ? "Add your first wallet" : "Position added"}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
            className={
                showReviewReceipt ? undefined : "border-t border-line-base"
            }
        >
            <div
                className={
                    showReviewReceipt
                        ? undefined
                        : "px-6 py-12 flex flex-col gap-10"
                }
            >
                {/* Headline + explanation */}
                {showIntro && (
                    <Heading
                        title="Start Earning"
                        titleVariant="h2"
                        subtitle={
                            <>
                                It looks like you don&rsquo;t have any addresses
                                monitored yet. Add your first wallet to start
                                earning rewards.
                            </>
                        }
                        subtitleVariant="body"
                        className="max-w-4xl"
                    />
                )}

                {/* How it works — a 2-panel progressive flow that mirrors the
                    add-position wizard. Left: numbered steps tracking progress.
                    Right: a vertical stack that grows step by step, collapsing
                    finished steps to summary rows. */}
                <div className="flex flex-col gap-4">
                    {showIntro && <Heading title="Process" titleVariant="h2" />}

                    <PositionWalkthrough
                        viewOnly={viewOnly}
                        onSubmitted={onSubmitted}
                        onPendingReviewChange={onPendingReviewChange}
                    />
                </div>
            </div>
        </motion.section>
    );
}
